import React from 'react';
import styled from 'styled-components';
import { GripVertical, X, Plus } from 'lucide-react';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const LegendCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  line-height: 1.5;

  strong {
    color: ${props => props.theme.colors.text};
    font-weight: ${props => props.theme.typography.weights.semiBold};
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: auto 1fr 1fr ${props => props.$hasGroup ? '0.7fr' : ''} ${props => props.$hasColor ? 'auto' : ''} auto;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.borderRadius.md};

  &:hover {
    background: ${props => props.theme.colors.background};
  }
`;

const Handle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 32px;
  color: ${props => props.theme.colors.muted};
  cursor: grab;
  opacity: 0.6;
  flex-shrink: 0;

  &:hover { opacity: 1; }
`;

const TextInput = styled.input`
  width: 100%;
  min-height: 36px;
  padding: 0 ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.tint}20;
  }

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }
`;

const ColorSwatch = styled.input.attrs({ type: 'color' })`
  width: 36px;
  height: 36px;
  padding: 2px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.backgroundSecondary};
  cursor: pointer;
  flex-shrink: 0;
`;

const DeleteBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.md};
  background: transparent;
  color: ${props => props.theme.colors.muted};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    color: ${props => props.theme.colors.danger};
    background: ${props => props.theme.colors.danger}15;
    border-color: ${props => props.theme.colors.danger}40;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.xs};
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  border: 1px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: transparent;
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: ${props => props.theme.typography.weights.semiBold};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    color: ${props => props.theme.colors.tint};
    background: ${props => props.theme.colors.tint}10;
  }
`;

const EXAMPLES = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'red', label: 'Red' },
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
];

export const OptionsEditor = ({
  options = [],
  onChange,
  supportsColor = false,
  supportsGroup = false,
  defaultColor = '#3b82f6',
}) => {
  const updateOption = (index, patch) => {
    const next = options.map((opt, i) => (i === index ? { ...opt, ...patch } : { ...opt }));
    onChange(next);
  };

  const removeOption = (index) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const addOption = () => {
    const nextIndex = options.length;
    const example = EXAMPLES[nextIndex % EXAMPLES.length] || {
      value: `option${nextIndex + 1}`,
      label: `Option ${nextIndex + 1}`,
    };
    const newOption = { ...example };
    if (supportsColor) newOption.color = defaultColor;
    onChange([...options, newOption]);
  };

  return (
    <Stack>
      <LegendCard>
        <strong>Value:</strong> stored in the database (e.g., "red")<br />
        <strong>Label:</strong> what users see on screen (e.g., "Red")
        {supportsColor && (<><br /><strong>Color:</strong> chip background color</>)}
        {supportsGroup && (<><br /><strong>Group:</strong> cascade tag — a field rule can filter these options by the value of a controlling field</>)}
      </LegendCard>

      {options.map((option, index) => (
        <Row key={index} $hasColor={supportsColor} $hasGroup={supportsGroup}>
          <Handle aria-hidden="true" title="Drag to reorder (coming soon)">
            <GripVertical size={16} />
          </Handle>
          <TextInput
            placeholder="value"
            value={option.value ?? ''}
            onChange={(e) => updateOption(index, { value: e.target.value })}
          />
          <TextInput
            placeholder="Label"
            value={option.label ?? ''}
            onChange={(e) => updateOption(index, { label: e.target.value })}
          />
          {supportsGroup && (
            <TextInput
              placeholder="Group"
              title="Cascade group tag (optional)"
              aria-label={`Cascade group for option ${index + 1}`}
              value={option.group ?? ''}
              onChange={(e) => updateOption(index, { group: e.target.value })}
            />
          )}
          {supportsColor && (
            <ColorSwatch
              title="Chip color"
              value={option.color || defaultColor}
              onChange={(e) => updateOption(index, { color: e.target.value })}
            />
          )}
          <DeleteBtn
            type="button"
            title="Remove option"
            aria-label="Remove option"
            onClick={() => removeOption(index)}
          >
            <X size={16} />
          </DeleteBtn>
        </Row>
      ))}

      <AddButton type="button" onClick={addOption}>
        <Plus size={14} />
        Add Option
      </AddButton>
    </Stack>
  );
};
